// This is a placeholder file for MiningPoolsSection
import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@stitches/react';
import { motion } from 'framer-motion';
import { WalletOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// 矿区配置常量 - 保持原有结构，名称将通过国际化翻译
export const MINING_ZONES = [
  { key: 'pakistan', nameKey: 'home.miningPools.zones.pakistan', yield: 165, flag: '🇵🇰', code: 'PK' },
  { key: 'russia', nameKey: 'home.miningPools.zones.russia', yield: 160, flag: '🇷🇺', code: 'RU' },
  { key: 'kentucky', nameKey: 'home.miningPools.zones.kentucky', yield: 150, flag: '🇺🇸', code: 'KY' },
  { key: 'texas', nameKey: 'home.miningPools.zones.texas', yield: 145, flag: '🇺🇸', code: 'TX' },
  { key: 'finland', nameKey: 'home.miningPools.zones.finland', yield: 130, flag: '🇫🇮', code: 'FI' },
];

// 区块容器样式
const SectionContainer = styled('section', {
  width: '100%',
  padding: '0 0 var(--spacing-xxl) 0',
  backgroundColor: 'var(--surface-color)',
  position: 'relative',
  overflow: 'hidden'
});

// 标题区域背景
const TitleBackground = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '180px',
  backgroundColor: 'var(--primary-light)',
  borderBottom: '0px solid var(--border-color)',
  zIndex: 0
});

// 内容容器样式
const ContentContainer = styled('div', {
  maxWidth: 'var(--container-width)',
  margin: '0 auto',
  padding: '0 var(--spacing-lg)',
  position: 'relative',
  zIndex: 1
});

// 区块标题样式
const SectionTitle = styled('h2', {
  fontSize: 'var(--font-size-xxl)',
  fontWeight: 'var(--font-weight-bold)',
  textAlign: 'center',
  marginBottom: 'var(--spacing-xl)',
  marginTop: 'var(--spacing-xxl)',
  color: 'var(--text-primary)'
});

// 矿机卡片滑动容器
const PoolCardsContainer = styled('div', {
  display: 'flex',
  gap: 'var(--spacing-lg)',
  marginBottom: 'var(--spacing-xxl)',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollBehavior: 'smooth',
  padding: '0 var(--spacing-lg) var(--spacing-lg) var(--spacing-lg)',

  // 自定义滚动条样式
  '&::-webkit-scrollbar': {
    height: '8px'
  },

  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px'
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '4px',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.5)'
    }
  },

  // 隐藏默认滚动条（Firefox）
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1)'
});

// 矿机卡片样式 - 深色主题
const PoolCard = styled(motion.div, {
  backgroundColor: '#151C2E',
  borderRadius: '15px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid #444',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
  position: 'relative',
  color: 'white',
  width: '320px',
  minWidth: '320px',
  flexShrink: 0,

  '&:hover': {
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.4)',
    borderColor: '#666'
  },

  '@media (max-width: 768px)': {
    width: '280px',
    minWidth: '280px'
  }
});

// 矿区头部信息
const MiningZoneHeader = styled('div', {
  padding: '16px 16px 12px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '100%'
});

// 左侧区域（国旗和PK）
const ZoneLeft = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
});

// 国旗和地区标识
const ZoneFlag = styled('span', {
  fontSize: '18px'
});

// PK标识
const ZoneCode = styled('span', {
  color: '#fff',
  fontSize: '14px',
  fontWeight: 'bold'
});

// 年化收益标签
const YieldBadge = styled('div', {
  background: '#D2691E',
  color: '#fff',
  fontSize: '12px',
  fontWeight: 'bold',
  padding: '4px 8px',
  borderRadius: '4px',
  display: 'inline-block'
});

// 矿机型号标题
const MinerModelTitle = styled('div', {
  padding: '0 16px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  fontWeight: 'medium',
  color: '#FFD700'
});

// 矿机规格容器
const MinerSpecsContainer = styled('div', {
  padding: '0 16px 16px'
});

// 规格网格
const SpecsGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '8px 12px'
});

// 单个规格项
const SpecItem = styled('div', {
  borderRadius: '6px',
  padding: '10px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',

  variants: {
    type: {
      hashrate: {
        backgroundColor: '#2d5f42'
      },
      power: {
        backgroundColor: '#5c2d2d'
      },
      algorithm: {
        backgroundColor: '#5c4a2d'
      },
      income: {
        backgroundColor: '#2d5f42'
      }
    }
  }
});

// 规格图标区域
const SpecIconArea = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  marginRight: '4px'
});

// 规格图标
const SpecIcon = styled('div', {
  fontSize: '12px',
  lineHeight: 1,

  variants: {
    type: {
      hashrate: { color: '#4ade80' },
      power: { color: '#f87171' },
      algorithm: { color: '#fbbf24' },
      income: { color: '#4ade80' }
    }
  }
});

// 规格值和标签容器
const SpecContent = styled('div', {
  flex: 1
});

// 规格值
const SpecValue = styled('div', {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'white',
  lineHeight: 1
});

// 规格标签
const SpecLabel = styled('div', {
  fontSize: 'var(--font-size-xs)',
  color: '#9CA3AF',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginTop: '2px'
});

// 数量选择器容器
const QuantitySelector = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  padding: '16px',
  borderTop: '1px solid #444',
  borderBottom: '1px solid #444'
});

// 数量选择器标签
const QuantityLabel = styled('span', {
  color: '#fff',
  fontSize: '14px'
});

// 数量控制容器
const QuantityControls = styled('div', {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#444',
  borderRadius: '4px',
  overflow: 'hidden'
});

// 数量控制按钮
const QuantityButton = styled('button', {
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#444',
  border: 'none',
  color: '#fff',
  fontSize: '16px',
  cursor: 'pointer',

  '&:hover:not(:disabled)': {
    backgroundColor: '#555'
  },

  '&:disabled': {
    color: '#666',
    cursor: 'not-allowed'
  }
});

// 数量输入框
const QuantityInput = styled('input', {
  width: '40px',
  height: '24px',
  backgroundColor: '#444',
  textAlign: 'center',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 'bold',
  border: 'none',
  outline: 'none',

  '&::placeholder': {
    color: '#666'
  }
});

// 价格信息容器
const PriceInfoContainer = styled('div', {
  padding: '16px',
  backgroundColor: '#2a2a2a'
});

// 价格行
const PriceRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '6px',

  '&:last-child': {
    marginBottom: 0
  }
});

// 价格标签
const PriceLabel = styled('span', {
  color: '#fff',
  fontSize: '14px'
});

// 价格值
const PriceValue = styled('span', {
  fontFamily: 'monospace',
  fontWeight: 'medium',

  variants: {
    type: {
      original: {
        color: '#888',
        textDecoration: 'line-through',
        fontSize: '14px'
      },
      final: {
        color: '#FFD700',
        fontSize: '16px',
        fontWeight: 'bold'
      }
    }
  }
});

// 购买按钮
const PurchaseButton = styled('button', {
  width: '100%',
  padding: '12px',
  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  color: '#000',
  border: 'none',
  borderRadius: '0 0 12px 12px',
  fontSize: '14px',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',

  '&:hover': {
    background: 'linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)',
    transform: 'translateY(-1px)'
  },

  '&:disabled': {
    background: '#666',
    color: '#ccc',
    cursor: 'not-allowed',
    transform: 'none'
  }
});

// 滑动导航容器
const SlideNavContainer = styled('div', {
  position: 'relative',
  width: '100%'
});

// 导航按钮已删除 - 当前未使用

// 分隔线
const Divider = styled('div', {
  width: '100%',
  height: '1px',
  backgroundColor: 'var(--divider-color)',
  margin: 'var(--spacing-xxl) 0 0 0'
});

/**
 * 选择矿机部分组件
 * 展示各种矿机类型
 * 使用深色主题设计
 */
const MiningPoolsSection: React.FC = () => {
  const { t } = useTranslation(); // 使用国际化钩子

  // 每个矿机的购买数量
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  // 滑动相关状态
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // 获取数量
  const getQuantity = (minerId: string) => quantities[minerId] || 1;

  // 设置数量
  const setQuantity = (minerId: string, quantity: number) => {
    setQuantities(prev => ({
      ...prev,
      [minerId]: Math.max(1, Math.min(1000, quantity))
    }));
  };

  // 计算价格
  const calculatePrices = (basePrice: number, quantity: number) => {
    const unitPrice = basePrice; // 单价
    const totalPrice = unitPrice * quantity; // 总价 = 单价 * 数量
    return {
      unitPrice: unitPrice.toFixed(2),
      totalPrice: totalPrice.toFixed(2)
    };
  };

  // 矿机数据 - 基于矿区配置生成，使用国际化翻译
  const miners = MINING_ZONES.map(zone => ({
    id: zone.key,
    name: t(zone.nameKey), // 使用国际化翻译的名称
    model: 'AntMiner S21 Pro',
    yield: zone.yield,
    flag: zone.flag,
    code: zone.code,
    basePrice: 55, // 统一基础价格
    specs: {
      hashRate: '200 TH/s',
      powerConsumption: '3250W',
      algorithm: 'SHA-256'
    },
    isSoldOut: false
  }));

  // 滑动导航函数 (未使用)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const slideToIndex = (index: number) => {
    if (containerRef.current) {
      const isMobile = window.innerWidth <= 768;
      const cardWidth = isMobile ? 280 + 24 : 320 + 24; // 卡片宽度 + 间距
      const scrollLeft = index * cardWidth;
      containerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };

  // 滑动导航函数已删除 - 当前未使用

  // 处理滚动事件来同步指示器
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isMobile = window.innerWidth <= 768;
      const cardWidth = isMobile ? 280 + 24 : 320 + 24;
      const scrollLeft = container.scrollLeft;
      const newIndex = Math.round(scrollLeft / cardWidth);

      if (newIndex !== currentSlide && newIndex >= 0 && newIndex < miners.length) {
        setCurrentSlide(newIndex);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentSlide, miners.length]);

  // 处理购买 - 跳转到购买页面
  const handlePurchase = (minerId: string) => {
    console.log('购买矿机:', minerId, '数量:', getQuantity(minerId));
    // 在新窗口中打开购买页面
    window.open('http://nft.bitengine.org/shop', '_blank', 'noopener,noreferrer');
  };

  // 卡片动画变体
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5
      }
    }),
    exit: { opacity: 0, y: -20 }
  };

  return (
    <SectionContainer id="mining-locations">
      <TitleBackground />
      <ContentContainer>
        <SectionTitle>{t('home.miningPools.title')}</SectionTitle>

        <SlideNavContainer>

          <PoolCardsContainer ref={containerRef}>
            {miners.map((miner, index) => {
              const quantity = getQuantity(miner.id);
              const prices = calculatePrices(miner.basePrice, quantity);
              const dailyIncome = ((miner.yield / 100 * parseFloat(prices.totalPrice)) / 365).toFixed(2);

              return (
                <PoolCard
                  key={miner.id}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  custom={index}
                  exit="exit"
                  layout
                >
                  {/* 矿区头部 */}
                  <MiningZoneHeader>
                    <ZoneLeft>
                      <ZoneFlag>{miner.flag}</ZoneFlag>
                      <ZoneCode>{miner.name}</ZoneCode>
                    </ZoneLeft>
                    <YieldBadge>{miner.yield}% 年化收益</YieldBadge>
                  </MiningZoneHeader>

                  {/* 矿机型号 */}
                  <MinerModelTitle>
                    ⚠️ {miner.model}
                  </MinerModelTitle>

                  {/* 矿机规格 */}
                  <MinerSpecsContainer>

                    <SpecsGrid>
                      <SpecItem type="hashrate">
                        <SpecIconArea>
                          <SpecIcon type="hashrate">📊</SpecIcon>
                          <SpecIcon type="hashrate">📊</SpecIcon>
                          <SpecIcon type="hashrate">📊</SpecIcon>
                        </SpecIconArea>
                        <SpecContent>
                          <SpecValue>{miner.specs.hashRate}</SpecValue>
                          <SpecLabel>HASHRATE</SpecLabel>
                        </SpecContent>
                      </SpecItem>

                      <SpecItem type="power">
                        <SpecIconArea>
                          <SpecIcon type="power">⚡</SpecIcon>
                          <SpecIcon type="power">⚡</SpecIcon>
                          <SpecIcon type="power">⚡</SpecIcon>
                        </SpecIconArea>
                        <SpecContent>
                          <SpecValue>{miner.specs.powerConsumption}</SpecValue>
                          <SpecLabel>POWER</SpecLabel>
                        </SpecContent>
                      </SpecItem>

                      <SpecItem type="algorithm">
                        <SpecIcon type="algorithm">🏆</SpecIcon>
                        <SpecContent>
                          <SpecValue>{miner.specs.algorithm}</SpecValue>
                          <SpecLabel>ALGORITHM</SpecLabel>
                        </SpecContent>
                      </SpecItem>

                      <SpecItem type="income">
                        <SpecIcon type="income">💰</SpecIcon>
                        <SpecContent>
                          <SpecValue style={{ color: '#4ade80' }}>+${dailyIncome}</SpecValue>
                          <SpecLabel>INCOME 24H*</SpecLabel>
                        </SpecContent>
                      </SpecItem>
                    </SpecsGrid>
                  </MinerSpecsContainer>

                  {/* 数量选择器 */}
                  <QuantitySelector>
                    <QuantityLabel>{t('common.quantity', '数量')}</QuantityLabel>
                    <QuantityControls>
                      <QuantityButton
                        onClick={() => setQuantity(miner.id, quantity - 1)}
                        disabled={quantity <= 1}
                      >
                        -
                      </QuantityButton>
                      <QuantityInput
                        type="number"
                        value={quantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          setQuantity(miner.id, value);
                        }}
                        min={1}
                        max={1000}
                        placeholder="1"
                      />
                      <QuantityButton
                        onClick={() => setQuantity(miner.id, quantity + 1)}
                        disabled={quantity >= 1000}
                      >
                        +
                      </QuantityButton>
                    </QuantityControls>
                  </QuantitySelector>

                  {/* 价格信息 */}
                  <PriceInfoContainer>
                    <PriceRow>
                      <PriceLabel>{t('common.unitPrice', '单价')}：</PriceLabel>
                      <PriceValue type="final">${prices.unitPrice} USDT</PriceValue>
                    </PriceRow>
                    <PriceRow>
                      <PriceLabel>{t('common.totalPrice', '总价')}：</PriceLabel>
                      <PriceValue type="final">${prices.totalPrice} USDT</PriceValue>
                    </PriceRow>
                  </PriceInfoContainer>

                  {/* 购买按钮 */}
                  <PurchaseButton
                    onClick={() => handlePurchase(miner.id)}
                    disabled={miner.isSoldOut}
                  >
                    <WalletOutlined /> USDT {t('home.miningPools.buy')}
                  </PurchaseButton>
                </PoolCard>
              );
            })}
          </PoolCardsContainer>
        </SlideNavContainer>



        <Divider />
      </ContentContainer>
    </SectionContainer>
  );
};

export default MiningPoolsSection;